import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateLoader } from '@ngx-translate/core';
import { from, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { selectCompanyPrefLang } from '@rpt-ngrx/selectors/company.selector';
import { selectSessionState } from '@rpt-ngrx/selectors/session.selector';
import { AgGridI18nService } from '@services/ag-grid-i18n/ag-grid-i18n.service';
import { CustomHttpClient } from '@services/http-client';

@Injectable({
    providedIn: 'root',
})
export class I18nLoader implements TranslateLoader {
    private SKIP_TRANSLATION_MODULES_LIST = ['WeeklySalesAndLaborHistoryModule'];

    constructor(
        private http: CustomHttpClient,
        private store: Store,
        private agGridI18nService: AgGridI18nService,
    ) {}

    getTranslation(module: string): Observable<any> {
        if (this.SKIP_TRANSLATION_MODULES_LIST.includes(module)) {
            return of([]);
        }

        const userLoginPrefLanguage = localStorage.getItem('global/userLocale');

        return this.store.select(selectSessionState).pipe(
            distinctUntilChanged(
                (prev, next) =>
                    prev.accessTokenSet === next.accessTokenSet && prev.session_ready === next.session_ready,
            ),
            filter(({ session_ready }) => !!session_ready),
            switchMap(({ user }) =>
                this.store.select(selectCompanyPrefLang).pipe(
                    distinctUntilChanged(),
                    switchMap((prefer_language) =>
                        this.http
                            .post('/translation', {
                                appVersion: '1.0.300',
                                moduleName: module,
                                companyLanguageId: prefer_language,
                                userLanguageId: userLoginPrefLanguage || user.preferred_language,
                            })
                            .pipe(
                                catchError(() => {
                                    return from(import('../../../assets/i18n/default.en.json'));
                                }),
                                map((translation) => {
                                    if (this.agGridI18nService && translation['AG_GRID_LOCALE_EN']) {
                                        this.agGridI18nService.agGridTranslationLib = translation['AG_GRID_LOCALE_EN'];
                                    }
                                    return translation;
                                }),
                            ),
                    ),
                ),
            ),
        );
    }
}
